import type { QueryFilter } from '@/types';

/**
 * Type guard to check if a filter is a logical group (AND/OR)
 */
export function isLogicalFilter(
  filter: QueryFilter
): filter is QueryFilter & { value: QueryFilter[] } {
  return filter.operator === 'AND' || filter.operator === 'OR';
}

/**
 * Check if a filter is a leaf filter (not a logical group)
 */
export function isLeafFilter(filter: QueryFilter): boolean {
  return !isLogicalFilter(filter);
}

/**
 * Calculate maximum nesting depth of a filter tree
 */
export function getMaxDepth(
  filter: QueryFilter,
  currentDepth: number = 0
): number {
  if (isLeafFilter(filter)) {
    return currentDepth;
  }

  const children = filter.value as QueryFilter[];
  if (!Array.isArray(children) || children.length === 0) {
    return currentDepth;
  }

  const childDepths = children.map(child =>
    getMaxDepth(child, currentDepth + 1)
  );
  return Math.max(...childDepths);
}

/**
 * Maximum allowed nesting depth for logical filters
 * Depth 0: Root level
 * Depth 1: First level of nesting (e.g., OR(filter))
 * Depth 2: Second level of nesting (e.g., OR(OR(filter)))
 * Beyond depth 2 is not allowed
 */
export const MAX_LOGICAL_DEPTH = 2;

/**
 * Get available operators based on current nesting depth
 * At depth 2, AND/OR are not allowed to prevent deeper nesting
 */
export function getAvailableOperators(depth: number): string[] {
  const BASE_OPERATORS = [
    '=', // Equality
    '<>', // Not equal (displays as "!=")
    '<', // Less than
    '<=', // Less than or equal
    '>', // Greater than
    '>=', // Greater than or equal
    'LIKE', // Pattern matching
    'STARTS WITH', // Prefix match
    'ENDS WITH', // Suffix match
    'CONTAINS', // Substring match
    'IN', // List membership
    'IS NULL', // Null check
    'IS NOT NULL', // Not null check
  ];
  const LOGICAL_OPERATORS = ['AND', 'OR'];

  if (depth >= MAX_LOGICAL_DEPTH) {
    return BASE_OPERATORS;
  }

  return [...BASE_OPERATORS, ...LOGICAL_OPERATORS];
}

/**
 * Convert a filter to a logical filter (AND/OR) with proper initialization
 * Replaces all properties and initializes value as an array with one empty child filter
 */
export function convertToLogicalFilter(newOperator: 'AND' | 'OR'): QueryFilter {
  return {
    field: '',
    operator: newOperator,
    value: [{ field: '', operator: '=', value: '' }],
  };
}

/**
 * Check if a filter should be converted from leaf to logical
 * Returns true when transitioning from a non-logical to logical operator
 */
export function shouldConvertToLogical(
  currentFilter: QueryFilter,
  updates: Partial<QueryFilter>
): boolean {
  const wasLogical = isLogicalFilter(currentFilter);
  const newOperator = updates.operator || currentFilter.operator;
  const willBeLogical = newOperator === 'AND' || newOperator === 'OR';
  return !wasLogical && willBeLogical;
}
